import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import TabelaPagamento from "./TabelaPagamento/TabelaPagamento";
import PaginationControls from "../Documentation/Analysis/ComponentsAnalysis/PaginationControls/PaginationControls";
import SearchExport from "../Documentation/Analysis/ComponentsAnalysis/SearchExport/SearchExport";
import FilterSectionPayment from "./FilterSectionPayment/FilterSectionPayment";
import HistoryPagamento from "./HistoryPagamento/HistoryPagamento";
import NotificationToast from "../../components/NotificationToast/NotificationToast";
import {
  ALL_PAYMENT_SITUATIONS,
  type PaymentHistoryEntry,
  type PaymentListItem,
  type SortConfig,
} from "./shared/paymentListTypes";
import {
  getPagamentoComprovanteDespesasById,
  getPagamentoEventosById,
  getPagamentoSolicitacoesByAno,
} from "../../services/pagamentoAvaliacaoService";
import { formatCurrencyBRL, normalizeWordChars } from "../../utils";
import { formatDateTimeExtratoList } from "../../utils/date";
import {
  formatSolicitacao,
  getAnoRequestParam,
} from "./shared/paymentListUtils";
import {
  extractAnosModeloPagamento,
  mapPagamentoSolicitacoesToList,
} from "./shared/mapPagamentoSolicitacoesToList";
import { mapPagamentoEventosToHistory } from "./shared/mapPagamentoEventosToHistory";
import "./Payment.css";
import "../Documentation/Analysis/ComponentsAnalysis/PaginationControls/PaginationControls.css";

const STORAGE_KEY = "payment_programacao_filters_v1";

// TODO: remover quando 2025 voltar a ser um ano de referência selecionável no pagamento.
const HIDDEN_YEARS = new Set([2025]);

// Oculta anos do seletor sem afetar o ano efetivamente carregado/retornado pela API.
const sanitizeYears = (anos: number[]) => {
  const visiveis = anos.filter((ano) => !HIDDEN_YEARS.has(ano));
  return visiveis.length > 0 ? visiveis : anos;
};

const readStoredYear = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 2026;
    const parsed = JSON.parse(raw);
    return Number.isFinite(parsed?.selectedYear) ? parsed.selectedYear : 2026;
  } catch {
    return 2026;
  }
};

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState<PaymentListItem[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [availableYears, setAvailableYears] = useState<number[]>(
    sanitizeYears([2026, 2025]),
  );
  const [selectedYear, setSelectedYear] = useState(() => readStoredYear());
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: null,
  });
  const [selectedSituation, setSelectedSituation] = useState<string[]>([
    ...ALL_PAYMENT_SITUATIONS,
  ]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState<PaymentHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const skipNextYearFetchRef = useRef(false);

  const applyListResponse = (
    response: Awaited<ReturnType<typeof getPagamentoSolicitacoesByAno>>,
    anoFallback: number,
  ) => {
    const anosModelo = extractAnosModeloPagamento(response);
    if (anosModelo.length > 0) {
      setAvailableYears(sanitizeYears(anosModelo));
    }
    const ano =
      Number(response?.anoReferenciaSelecionado) || anoFallback;
    return {
      ano,
      rows: mapPagamentoSolicitacoesToList(response?.pagamentos, ano),
    };
  };

  const loadRowsForYear = async (ano: number) => {
    const response = await getPagamentoSolicitacoesByAno(
      getAnoRequestParam(ano),
    );
    return applyListResponse(response, ano).rows;
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed?.selectedSituation)) {
        const stored = parsed.selectedSituation.filter(
          (s: unknown) => typeof s === "string",
        ) as string[];
        const sanitized = stored.filter((s) =>
          (ALL_PAYMENT_SITUATIONS as readonly string[]).includes(s),
        );
        setSelectedSituation(
          sanitized.length > 0 ? sanitized : [...ALL_PAYMENT_SITUATIONS],
        );
      }

      if (Number.isFinite(parsed?.selectedYear)) {
        setSelectedYear(parsed.selectedYear);
      }

      if (typeof parsed?.searchTerm === "string") {
        setSearchTerm(parsed.searchTerm);
      }

      if (Number.isFinite(parsed?.currentPage)) {
        setCurrentPage(parsed.currentPage);
      }

      if (Number.isFinite(parsed?.itemsPerPage)) {
        setItemsPerPage(parsed.itemsPerPage);
      }
    } catch (e) {
      console.warn("Não foi possível restaurar filtros de pagamento:", e);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          selectedSituation,
          selectedYear,
          searchTerm,
          currentPage,
          itemsPerPage,
          sortConfig,
        }),
      );
    } catch (e) {
      console.warn("Não foi possível salvar filtros de pagamento:", e);
    }
  }, [
    selectedSituation,
    selectedYear,
    searchTerm,
    currentPage,
    itemsPerPage,
    sortConfig,
  ]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        let anoSelecionado = readStoredYear();
        skipNextYearFetchRef.current = true;

        let response = await getPagamentoSolicitacoesByAno(
          getAnoRequestParam(anoSelecionado),
        );
        let mapped = applyListResponse(response, anoSelecionado);

        if (mapped.ano !== anoSelecionado) {
          response = await getPagamentoSolicitacoesByAno(
            getAnoRequestParam(mapped.ano),
          );
          mapped = applyListResponse(response, mapped.ano);
        }

        setSelectedYear(mapped.ano);
        setData(mapped.rows);
      } catch (error) {
        console.error("Erro ao buscar lista de pagamento:", error);
        skipNextYearFetchRef.current = true;
        setData([]);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (initialLoading || !selectedYear) return;
    if (skipNextYearFetchRef.current) {
      skipNextYearFetchRef.current = false;
      return;
    }

    const fetchDataByYear = async () => {
      setLoading(true);
      try {
        setData(await loadRowsForYear(selectedYear));
      } catch (error) {
        console.error("Erro ao buscar pagamento por ano:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDataByYear();
  }, [selectedYear, initialLoading]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedSituation, itemsPerPage, searchTerm]);

  useEffect(() => {
    const state = location.state as
      | { toastMessage?: string; toastType?: string }
      | null;
    if (!state?.toastMessage) return;

    setToastType(state.toastType || "success");
    setToastMessage(state.toastMessage);
    setShowToast(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  const filteredData = useMemo(() => {
    const searchNormalized = normalizeWordChars(searchTerm);
    const selectedNormalized = selectedSituation.map((s) =>
      (s || "").toLowerCase().trim(),
    );

    return data.filter((item) => {
      const isAllSelected =
        selectedSituation.length === 0 ||
        selectedSituation.length === ALL_PAYMENT_SITUATIONS.length;
      const isSituationMatch =
        isAllSelected ||
        selectedNormalized.includes(
          (item.situacaoPagamento || "").toLowerCase().trim(),
        );

      const matchesText =
        (item.sigla || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        normalizeWordChars(item.cnpj).includes(searchNormalized);

      return isSituationMatch && matchesText;
    });
  }, [data, selectedSituation, searchTerm]);

  const sortedData = useMemo(() => {
    const { key, direction } = sortConfig;
    if (!key || !direction) return [...filteredData];

    const toTS = (dateString: string | null | undefined) => {
      if (!dateString) return 0;
      const d = new Date(dateString);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    };

    const dataCopy = [...filteredData];
    dataCopy.sort((a, b) => {
      if (key === "dataEmissao") {
        return direction === "asc"
          ? toTS(a.dataEmissao) - toTS(b.dataEmissao)
          : toTS(b.dataEmissao) - toTS(a.dataEmissao);
      }

      if (key === "assinatura") {
        return direction === "asc"
          ? toTS(a.dataAssinaturaSolicitacao) - toTS(b.dataAssinaturaSolicitacao)
          : toTS(b.dataAssinaturaSolicitacao) - toTS(a.dataAssinaturaSolicitacao);
      }

      if (key === "valorBruto" || key === "solicitacao") {
        const aNum = Number(a[key] ?? 0);
        const bNum = Number(b[key] ?? 0);
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      const aVal = (a[key as keyof PaymentListItem] ?? "").toString();
      const bVal = (b[key as keyof PaymentListItem] ?? "").toString();
      return direction === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });

    return dataCopy;
  }, [filteredData, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
  const currentItems = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const startResult =
    sortedData.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endResult = Math.min(currentPage * itemsPerPage, sortedData.length);

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" | null = "asc";
    let newKey: string | null = key;
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
      newKey = null;
    }
    setSortConfig({ key: newKey, direction });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc"
        ? "▲"
        : sortConfig.direction === "desc"
          ? "▼"
          : "↕";
    }
    return "↕";
  };

  const handleSituationFilter = (value: string | string[]) => {
    if (Array.isArray(value)) {
      setSelectedSituation(value);
      return;
    }
    setSelectedSituation((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value],
    );
  };

  const resolvePagamentoDespesasAvaliacaoId = async (
    item: PaymentListItem,
  ): Promise<number> => {
    const fromList = Number(item?.idPagamento) || 0;
    if (fromList) return fromList;

    const comprovanteId = Number(item?.idSolicitacao) || 0;
    if (!comprovanteId) return 0;

    try {
      const detalhe = await getPagamentoComprovanteDespesasById(comprovanteId);
      return Number(detalhe?.idPagamento) || comprovanteId;
    } catch {
      return comprovanteId;
    }
  };

  const handleOpenHistory = async (item: PaymentListItem) => {
    setHistoryData([]);
    setShowHistoryModal(true);
    setLoadingHistory(true);
    try {
      const pagamentoId = await resolvePagamentoDespesasAvaliacaoId(item);
      const eventos = await getPagamentoEventosById(pagamentoId);
      setHistoryData(mapPagamentoEventosToHistory(eventos));
    } catch (error) {
      console.error("Erro ao carregar histórico de pagamento:", error);
      setHistoryData([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const exportData = useMemo(
    () =>
      filteredData.map((i) => ({
        CNPJ: i.cnpj,
        SIGLA: i.sigla,
        SITUACAO: i.situacaoPagamento || "-",
        SOLICITACAO: formatSolicitacao(i.solicitacao),
        ASSINATURA_FORMULARIO: i.dataAssinaturaSolicitacao
          ? formatDateTimeExtratoList(i.dataAssinaturaSolicitacao)
          : "-",
        DATA_EMISSAO: i.dataEmissao
          ? formatDateTimeExtratoList(i.dataEmissao)
          : "-",
        VALOR_BRUTO:
          i.valorBruto == null ? "-" : formatCurrencyBRL(i.valorBruto),
      })),
    [filteredData],
  );

  if (initialLoading || loading) return <PageLoading />;

  return (
    <Container fluid className="payment-container">
      <h1 className="payment-title">LISTA DE SINDICATOS - CONTAS A PAGAR</h1>

      <div className="filters-actions-container shipping-filters-actions-container">
        <FilterSectionPayment
          selectedSituation={selectedSituation}
          handleSituationFilter={handleSituationFilter}
          allSituations={ALL_PAYMENT_SITUATIONS}
        />

        <SearchExport
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          availableYears={availableYears}
          yearTooltip="No momento, é possível visualizar apenas os dados de 2026."
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          exportData={exportData}
          fileName={`ListaSindicatosPagamento_${selectedYear}`}
        />
      </div>

      <TabelaPagamento
        currentItems={currentItems}
        handleSort={handleSort}
        getSortIcon={getSortIcon}
        onOpenHistory={handleOpenHistory}
        selectedYear={selectedYear}
        formatCurrency={formatCurrencyBRL}
      />

      <div className="pagination-wrapper">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          startResult={startResult}
          endResult={endResult}
          totalItems={sortedData.length}
        />

        <HistoryPagamento
          show={showHistoryModal}
          onHide={() => {
            setShowHistoryModal(false);
            setHistoryData([]);
            setLoadingHistory(false);
          }}
          historyData={historyData}
          loading={loadingHistory}
        />
      </div>

      <NotificationToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type={toastType}
      />
    </Container>
  );
};

export default Payment;
